import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { MapPin, Star, Wifi, Car, Utensils, Waves, Dumbbell, Users, Phone, Mail, Globe, Calendar } from "lucide-react";

interface Hotel {
  id: string;
  name: string;
  type: string;
  location: string;
  description: string;
  longDescription: string;
  features: string[];
  amenities: { icon: any; name: string }[];
  rooms: number;
  rating: number;
  contact: {
    phone: string;
    email: string;
    website: string;
  };
  images: string[];
  priceRange: string;
  openingYear: number;
}

interface HotelDetailsModalProps {
  hotel: Hotel | null;
  isOpen: boolean;
  onClose: () => void;
}

export const HotelDetailsModal = ({ hotel, isOpen, onClose }: HotelDetailsModalProps) => {
  if (!hotel) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-foreground mb-2">
            {hotel.name}
          </DialogTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{hotel.location}</span>
            </div>
            <Badge variant="secondary">{hotel.type}</Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Photo Gallery */}
          <div className="w-full">
            <h3 className="text-lg font-medium mb-4">Photo Gallery</h3>
            <div className="relative">
              <Carousel className="w-full">
                <CarouselContent className="-ml-2 md:-ml-4">
                  {hotel.images.map((image, index) => (
                    <CarouselItem key={index} className="pl-2 md:pl-4">
                      <Card>
                        <CardContent className="p-0">
                          <div className="aspect-[16/9] bg-gradient-to-br from-muted/20 to-muted/40 rounded-lg overflow-hidden">
                            <img 
                              src={image} 
                              alt={`${hotel.name} - Image ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <div className="hidden w-full h-full flex items-center justify-center">
                              <div className="text-center text-muted-foreground">
                                <Globe className="w-12 h-12 mx-auto mb-2" />
                                <p className="text-sm">Image unavailable</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {hotel.images.length > 1 && (
                  <>
                    <CarouselPrevious className="left-2" />
                    <CarouselNext className="right-2" />
                  </>
                )}
              </Carousel>
            </div>
          </div>

          {/* Hotel Information Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* About */}
            <div>
              <h3 className="text-lg font-medium mb-3">About</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {hotel.longDescription}
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>{hotel.rooms ? `${hotel.rooms} Rooms` : "Details Coming Soon"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>Opened in {hotel.openingYear}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 text-muted-foreground" />
                  <span>{hotel.priceRange}</span>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div>
              <h3 className="text-lg font-medium mb-3">Amenities</h3>
              <div className="grid grid-cols-2 gap-3">
                {hotel.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <amenity.icon className="w-4 h-4 text-muted-foreground" />
                    <span>{amenity.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Features */}
          <div>
            <h3 className="text-lg font-medium mb-3">Key Features</h3>
            <div className="flex flex-wrap gap-2">
              {hotel.features.map((feature, index) => (
                <Badge key={index} variant="outline">
                  {feature}
                </Badge>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-muted/20 rounded-lg p-4">
            <h3 className="text-lg font-medium mb-3">Contact Information</h3>
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{hotel.contact.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{hotel.contact.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span>{hotel.contact.website}</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};